/**
 * External Integrations Dashboard Page
 *
 * Platform-level monitoring for external system integrations
 * Requires special permission: platform.external_integrations.manage
 *
 * Features:
 * - Service account management (create, revoke, view)
 * - Ingestion metrics with granular time-series (10s/1m/1h/1d)
 * - Error breakdown and failure analysis
 * - Real-time alerts for suspicious activity
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/hooks/useAuth';
import {
  CloudArrowUpIcon,
  ServerStackIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  PlusIcon,
  EyeIcon,
  EyeSlashIcon,
  ShieldExclamationIcon,
  BuildingOfficeIcon,
  TrashIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MagnifyingGlassIcon,
  BellAlertIcon,
  CheckIcon,
  XMarkIcon,
  ClipboardDocumentIcon,
  KeyIcon,
  RectangleStackIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PencilSquareIcon,
  PowerIcon,
  BoltIcon
} from '@heroicons/react/24/outline';
import { PageTransition, PageTransitionItem } from '@/components/ui/PageTransition';
import { DataTable, ColumnDefinition, ActionDefinition, SortState } from '@/components/shared/DataTable';
import { useEhrRegistry } from '@/hooks/useEhrRegistry';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

/**
 * Extract a human-readable message from an API error body. The Joi `validate`
 * middleware returns { error: 'VALIDATION_ERROR', message, details: [{message}] },
 * so the top-level `error` is a machine code — prefer the specific field messages
 * (details) then `message`, falling back to `error` for hand-written responses.
 */
function extractApiError(data: any, fallback: string): string {
  if (data) {
    if (Array.isArray(data.details) && data.details.length > 0) {
      const joined = data.details.map((d: any) => d?.message).filter(Boolean).join('; ');
      if (joined) return joined;
    }
    if (typeof data.message === 'string' && data.message && data.message !== 'Request validation failed') {
      return data.message;
    }
    if (typeof data.error === 'string' && data.error && data.error !== 'VALIDATION_ERROR') {
      return data.error;
    }
  }
  return fallback;
}

// Types
interface DashboardSummary {
  totalServiceAccounts: number;
  activeServiceAccounts: number;
  totalIngestions24h: number;
  successRate24h: number;
  avgLatencyMs: number;
  activeAlerts: number;
  ingestionsAllTime: number;
  errorRate24h: number;
}

interface ServiceAccountWithStats {
  id: string;
  name: string;
  clientId: string;
  organizationId?: string;
  organizationName?: string;
  partnerId?: string;
  partnerName?: string;
  externalSystemId?: string;
  scope?: 'organization' | 'partner' | 'ehr';
  isActive: boolean;
  expiresAt?: string;
  createdAt: string;
  lastUsedAt?: string;
  permissions: string[];
  stats: {
    totalIngestions24h: number;
    successRate24h: number;
    failureCount24h: number;
    avgLatencyMs: number;
  };
}

interface IngestionTrendPoint {
  timestamp: string;
  success: number;
  failure: number;
  total: number;
  avgLatencyMs: number;
}

interface ErrorBreakdown {
  errorCode: string;
  count: number;
  percentage: number;
  lastOccurrence: string;
}

interface RecentFailure {
  id: string;
  timestamp: string;
  serviceAccountId: string;
  serviceAccountName?: string;
  organizationName?: string;
  externalEncounterId: string;
  errorCode: string;
  errorMessage: string;
  httpStatus: number;
  rawPayload?: Record<string, unknown>;
}

interface Alert {
  id: string;
  serviceAccountId: string;
  serviceAccountName?: string;
  organizationId: string;
  organizationName?: string;
  type: 'high_failure_rate' | 'unauthorized_ip' | 'rate_limit_exceeded' | 'suspicious_activity' | 'account_expired' | 'unusual_volume';
  severity: 'warning' | 'critical';
  title: string;
  message: string;
  metadata: Record<string, unknown>;
  timestamp: string;
  acknowledged: boolean;
  acknowledgedBy?: string;
  acknowledgedAt?: string;
  isResolved: boolean;
}

interface Organization {
  id: string;
  name: string;
}

interface Partner {
  id: string;
  name: string;
  slug?: string;
  status?: string;
}

// EHR registry entry as returned by the platform management endpoint
// (GET /api/v1/platform/external-integrations/ehrs).
interface EhrSystem {
  slug: string;
  displayName: string;
  vendor?: string | null;
  authPath?: string | null;
  healthCheckPath?: string | null;
  capabilities?: Record<string, Record<string, boolean>> | null;
  rateLimitRpm?: number | null;
  rateLimitBurst?: number | null;
  tenantMappingRequired?: boolean;
  tenantMappingFields?: string[] | null;
  featureFlags?: Record<string, boolean> | null;
  isActive: boolean;
  defaultWebhookId?: string | null;
  connectorStatus: 'ready' | 'none';
}

// A shared, standalone outbound-delivery webhook (External Integrations > Webhooks).
interface Webhook {
  id: string;
  name: string;
  url: string;
  events: string[];
  isActive: boolean;
  retryPolicy: { maxRetries: number; retryDelayMs: number; backoffMultiplier: number };
  lastTriggered?: string | null;
  totalDeliveries: number;
  successfulDeliveries: number;
  failedDeliveries: number;
  assignedOrganizationCount: number;
}

const WEBHOOK_EVENT_OPTIONS = [
  'clinicalResults.available',
  'appointment.created',
  'appointment.updated',
  'appointment.deleted',
  'transcription.completed',
  'transcription.failed',
  'recording.uploaded',
  'patient.created',
  'patient.updated',
] as const;

// Capability matrix shape used by the EHR create/edit modal.
const EHR_CAPABILITY_RESOURCES = ['patients', 'appointments'] as const;
const EHR_CAPABILITY_OPERATIONS = ['read', 'create', 'update', 'delete'] as const;

// Mirrors the backend's DEFAULT_EHR_CAPABILITIES (read-only patients +
// appointments). Seeding create mode with this shape keeps the Advanced matrix
// honest: what the operator sees is exactly what an untouched create stores.
const DEFAULT_CREATE_CAPABILITIES: Record<string, Record<string, boolean>> = {
  patients: { read: true, create: false, update: false, delete: false },
  appointments: { read: true, create: false, update: false, delete: false },
};

type TabType = 'overview' | 'service-accounts' | 'ingestion' | 'ehrs' | 'webhooks';

// The Service Accounts tab shows live accounts by default and revoked ones in their own view.
// Expired accounts are live but cannot get tokens, so they are listed first, as attention items,
// for someone to renew or revoke.
type ServiceAccountListStatus = 'active' | 'expired' | 'revoked';
type ServiceAccountView = 'active' | 'revoked';
const SERVICE_ACCOUNT_VIEWS: ServiceAccountView[] = ['active', 'revoked'];
const SERVICE_ACCOUNT_VIEW_LABELS: Record<ServiceAccountView, string> = { active: 'Active', revoked: 'Revoked' };
// The Webhooks tab lists active and inactive webhooks apart, a page at a time.
type WebhookView = 'active' | 'inactive';
const WEBHOOK_VIEWS: WebhookView[] = ['active', 'inactive'];
const WEBHOOK_VIEW_LABELS: Record<WebhookView, string> = { active: 'Active', inactive: 'Inactive' };
// Only a webhook nobody uses can be deleted; the server checks again and answers 409 otherwise.
const isDeletableWebhook = (w: Webhook) => !w.isActive && w.assignedOrganizationCount === 0 && w.totalDeliveries === 0;
type Granularity = '10s' | '1m' | '1h' | '1d';

// Utility functions
const formatNumber = (value: number | undefined | null): string => {
  return (value ?? 0).toLocaleString();
};

const formatPercent = (value: number | undefined | null): string => {
  return `${(value ?? 0).toFixed(1)}%`;
};

const formatLatency = (value: number | undefined | null): string => {
  const v = value ?? 0;
  if (v >= 1000) {
    return `${(v / 1000).toFixed(2)}s`;
  }
  return `${Math.round(v)}ms`;
};

const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatRelativeTime = (dateString: string | undefined): string => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

// Access Denied Component
const AccessDenied: React.FC<{ reason: 'role' | 'permission' }> = ({ reason }) => (
  <PageTransition className="flex items-center justify-center min-h-[60vh]">
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center max-w-md"
    >
      <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center mb-6">
        <LockClosedIcon className="w-10 h-10 text-purple-600" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-3">Access Denied</h2>
      <p className="text-gray-600 mb-6">
        {reason === 'role'
          ? 'External Integrations is only available to platform administrators.'
          : 'You need the platform.external_integrations.manage permission to access this dashboard. Contact a system administrator to request access.'}
      </p>
      <div className="inline-flex items-center gap-2 text-sm text-purple-600 bg-purple-50 px-4 py-2 rounded-full">
        <ExclamationTriangleIcon className="w-4 h-4" />
        Required: platform.external_integrations.manage
      </div>
    </motion.div>
  </PageTransition>
);

// Stat Card Component
const StatCard: React.FC<{
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ElementType;
  trend?: { value: number; isPositive: boolean };
  color: 'purple' | 'pink' | 'emerald' | 'amber' | 'red';
  delay?: number;
}> = ({ title, value, subtitle, icon: Icon, trend, color, delay = 0 }) => {
  const colorClasses = {
    purple: 'from-purple-500 to-purple-600',
    pink: 'from-pink-500 to-pink-600',
    emerald: 'from-emerald-500 to-emerald-600',
    amber: 'from-amber-500 to-amber-600',
    red: 'from-red-500 to-red-600'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="relative group"
    >
      <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/50 shadow-lg shadow-purple-500/5 overflow-hidden">
        <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${colorClasses[color]} opacity-5 rounded-full -translate-y-1/2 translate-x-1/2`} />

        <div className="flex items-start justify-between relative">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-500 tracking-wide uppercase">{title}</p>
            <p className="mt-2 text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              {value}
            </p>
            {subtitle && (
              <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
            )}
            {trend && (
              <div className={`mt-3 inline-flex items-center gap-1 text-sm font-medium ${trend.isPositive ? 'text-emerald-600' : 'text-rose-600'}`}>
                {trend.isPositive ? (
                  <ArrowTrendingUpIcon className="w-4 h-4" />
                ) : (
                  <ArrowTrendingDownIcon className="w-4 h-4" />
                )}
                {Math.abs(trend.value).toFixed(1)}% vs last period
              </div>
            )}
          </div>
          <div className={`p-3 rounded-xl bg-gradient-to-br ${colorClasses[color]} shadow-lg`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Alert Badge Component
const AlertBadge: React.FC<{ severity: 'warning' | 'critical'; count?: number }> = ({ severity, count }) => {
  const colors = severity === 'critical'
    ? 'bg-red-100 text-red-700 ring-red-500/20'
    : 'bg-amber-100 text-amber-700 ring-amber-500/20';

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ring-1 ${colors}`}>
      <ShieldExclamationIcon className="w-3 h-3" />
      {severity === 'critical' ? 'Critical' : 'Warning'}
      {count !== undefined && count > 0 && <span>({count})</span>}
    </span>
  );
};

// Status Badge Component
const StatusBadge: React.FC<{ active: boolean; expired?: boolean }> = ({ active, expired }) => {
  if (expired) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 ring-1 ring-gray-500/20">
        <ClockIcon className="w-3 h-3" />
        Expired
      </span>
    );
  }

  return active ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 ring-1 ring-emerald-500/20">
      <CheckCircleIcon className="w-3 h-3" />
      Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 ring-1 ring-red-500/20">
      <XCircleIcon className="w-3 h-3" />
      Revoked
    </span>
  );
};

// Granularity Selector Component
const GranularitySelector: React.FC<{
  value: Granularity;
  onChange: (g: Granularity) => void;
}> = ({ value, onChange }) => {
  const options: { value: Granularity; label: string }[] = [
    { value: '10s', label: '10s' },
    { value: '1m', label: '1m' },
    { value: '1h', label: '1h' },
    { value: '1d', label: '1d' }
  ];

  return (
    <div className="inline-flex bg-gray-100 rounded-lg p-0.5">
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
            value === opt.value
              ? 'bg-white text-purple-700 shadow-sm'
              : 'text-gray-600 hover:text-purple-600'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
};

// Trend Chart Component
const TrendChart: React.FC<{
  data: IngestionTrendPoint[];
  height?: number;
}> = ({ data, height = 200 }) => {
  if (!data.length) return null;

  const maxValue = Math.max(...data.map(d => d.total));
  if (maxValue === 0) {
    return (
      <div className="flex items-center justify-center h-[200px] text-gray-400">
        No ingestion data for this period
      </div>
    );
  }

  const points = data.map((d, i) => ({
    x: data.length > 1 ? (i / (data.length - 1)) * 100 : 50,
    ySuccess: 100 - (d.success / maxValue) * 100,
    yTotal: 100 - (d.total / maxValue) * 100
  }));

  const successPath = points.length > 1
    ? `M ${points[0].x} ${points[0].ySuccess} ${points.slice(1).map(p => `L ${p.x} ${p.ySuccess}`).join(' ')}`
    : `M ${points[0].x} ${points[0].ySuccess}`;

  const totalPath = points.length > 1
    ? `M ${points[0].x} ${points[0].yTotal} ${points.slice(1).map(p => `L ${p.x} ${p.yTotal}`).join(' ')}`
    : `M ${points[0].x} ${points[0].yTotal}`;

  return (
    <div className="relative" style={{ height }}>
      <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        {/* Grid lines */}
        {[0, 25, 50, 75, 100].map(y => (
          <line
            key={y}
            x1="0"
            y1={y}
            x2="100"
            y2={y}
            stroke="#e5e7eb"
            strokeWidth="0.5"
            vectorEffect="non-scaling-stroke"
          />
        ))}

        {/* Total area fill */}
        <motion.path
          d={`${totalPath} L 100 100 L 0 100 Z`}
          fill="url(#totalGradient)"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        />

        {/* Success area fill */}
        <motion.path
          d={`${successPath} L 100 100 L 0 100 Z`}
          fill="url(#successGradient)"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        />

        {/* Total line */}
        <motion.path
          d={totalPath}
          fill="none"
          stroke="#8B5CF6"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1 }}
        />

        {/* Success line */}
        <motion.path
          d={successPath}
          fill="none"
          stroke="#10B981"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
        />

        {/* Gradients */}
        <defs>
          <linearGradient id="totalGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="successGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#10B981" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>

      {/* Legend */}
      <div className="absolute top-2 right-2 flex gap-3 text-xs bg-white/80 backdrop-blur-sm rounded-lg p-2">
        <div className="flex items-center gap-1">
          <div className="w-3 h-0.5 bg-purple-500 rounded" />
          <span className="text-gray-600">Total</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-0.5 bg-emerald-500 rounded" />
          <span className="text-gray-600">Success</span>
        </div>
      </div>

      {/* X-axis labels */}
      <div className="absolute bottom-0 left-0 right-0 flex justify-between text-xs text-gray-500 -mb-6">
        {data.filter((_, i) => i % Math.ceil(data.length / 5) === 0 || i === data.length - 1).map((d, i) => (
          <span key={i}>{new Date(d.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
        ))}
      </div>
    </div>
  );
};

// Error Breakdown Chart
const ErrorBreakdownChart: React.FC<{ data: ErrorBreakdown[] }> = ({ data }) => {
  if (!data.length) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-400">
        No errors in this period
      </div>
    );
  }

  const colors = ['#EF4444', '#F59E0B', '#8B5CF6', '#EC4899', '#6366F1'];
  const maxCount = Math.max(...data.map(d => d.count));

  return (
    <div className="space-y-3">
      {data.slice(0, 5).map((error, index) => (
        <motion.div
          key={error.errorCode}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.05 }}
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium text-gray-700 truncate max-w-[60%]">{error.errorCode}</span>
            <span className="text-sm font-semibold text-gray-900">{formatNumber(error.count)}</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${(error.count / maxCount) * 100}%` }}
              transition={{ delay: index * 0.05 + 0.2, duration: 0.5 }}
              className="h-full rounded-full"
              style={{ backgroundColor: colors[index % colors.length] }}
            />
          </div>
        </motion.div>
      ))}
    </div>
  );
};

// Create Service Account Modal
const CreateServiceAccountModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  organizations: Organization[];
  partners: Partner[];
  onCreated: () => void;
}> = ({ isOpen, onClose, organizations, partners, onCreated }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scopeType, setScopeType] = useState<'organization' | 'partner' | 'ehr'>('organization');
  const [organizationId, setOrganizationId] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [externalSystemId, setExternalSystemId] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(90);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<{ clientId: string; clientSecret: string } | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied] = useState<'id' | 'secret' | null>(null);

  // Data-driven EHR systems for the EHR-scope dropdown (Workstream B registry).
  const { systems: ehrSystems } = useEhrRegistry();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/v1/platform/external-integrations/service-accounts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name,
          description: description || undefined,
          ...(scopeType === 'organization'
            ? { organizationId }
            : scopeType === 'partner'
              ? { partnerId }
              : { externalSystemId }),
          expiresInDays
        })
      });

      const data = await response.json();
      if (data.success) {
        setCredentials({
          clientId: data.data.clientId,
          clientSecret: data.data.clientSecret
        });
        onCreated();
      } else {
        setError(extractApiError(data, 'Failed to create service account'));
      }
    } catch (err) {
      setError('Failed to create service account');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (type: 'id' | 'secret', value: string) => {
    await navigator.clipboard.writeText(value);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setScopeType('organization');
    setOrganizationId('');
    setPartnerId('');
    setExternalSystemId('');
    setExpiresInDays(90);
    setCredentials(null);
    setError(null);
    onClose();
  };

  const selectedPartnerOrgs = partnerId
    ? organizations.filter(o => (o as any).partnerId === partnerId)
    : [];

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-pink-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <KeyIcon className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {credentials ? 'Service Account Created' : 'Create Service Account'}
              </h3>
            </div>
            <button onClick={handleClose} className="p-2 hover:bg-white/50 rounded-lg transition-colors">
              <XMarkIcon className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {credentials ? (
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">Save these credentials now!</p>
                    <p className="mt-1">The client secret will only be shown once and cannot be retrieved later.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-gray-100 rounded-lg text-sm font-mono break-all overflow-hidden">
                      {credentials.clientId}
                    </code>
                    <button
                      onClick={() => handleCopy('id', credentials.clientId)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      {copied === 'id' ? (
                        <CheckIcon className="w-5 h-5 text-emerald-600" />
                      ) : (
                        <ClipboardDocumentIcon className="w-5 h-5 text-gray-500" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client Secret</label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-gray-100 rounded-lg text-sm font-mono break-all overflow-hidden">
                      {showSecret ? credentials.clientSecret : '•'.repeat(32)}
                    </code>
                    <button
                      onClick={() => setShowSecret(!showSecret)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      {showSecret ? (
                        <EyeSlashIcon className="w-5 h-5 text-gray-500" />
                      ) : (
                        <EyeIcon className="w-5 h-5 text-gray-500" />
                      )}
                    </button>
                    <button
                      onClick={() => handleCopy('secret', credentials.clientSecret)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      {copied === 'secret' ? (
                        <CheckIcon className="w-5 h-5 text-emerald-600" />
                      ) : (
                        <ClipboardDocumentIcon className="w-5 h-5 text-gray-500" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <button
                onClick={handleClose}
                className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
              >
                I've saved the credentials
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder="CareTracker Production"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Production service account for CareTracker integration"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Scope *</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setScopeType('ehr'); setOrganizationId(''); setPartnerId(''); }}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                      scopeType === 'ehr'
                        ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    EHR (all orgs)
                  </button>
                  <button
                    type="button"
                    onClick={() => { setScopeType('organization'); setPartnerId(''); setExternalSystemId(''); }}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                      scopeType === 'organization'
                        ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Organization
                  </button>
                  <button
                    type="button"
                    onClick={() => { setScopeType('partner'); setOrganizationId(''); setExternalSystemId(''); }}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                      scopeType === 'partner'
                        ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Partner (legacy)
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {scopeType === 'ehr'
                    ? 'Recommended: one credential bootstraps encounters for every org on this EHR, across all partners. Target org is resolved per-request from (externalSystemId, externalTenantId).'
                    : scopeType === 'organization'
                      ? 'Legacy: this service account is bound to a single organization.'
                      : 'Legacy: one credential bootstraps encounters for any org under the partner. Target org is resolved per-request from the inbound externalTenantId.'}
                </p>
              </div>

              {scopeType === 'organization' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Organization *</label>
                  <select
                    value={organizationId}
                    onChange={(e) => setOrganizationId(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Select organization...</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>
              ) : scopeType === 'partner' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Partner *</label>
                  <select
                    value={partnerId}
                    onChange={(e) => setPartnerId(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Select partner...</option>
                    {partners.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  {partnerId && selectedPartnerOrgs.length > 0 && (
                    <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-600">
                      <p className="font-medium mb-1">Will bootstrap for {selectedPartnerOrgs.length} current org(s):</p>
                      <ul className="list-disc list-inside">
                        {selectedPartnerOrgs.slice(0, 5).map(o => <li key={o.id}>{o.name}</li>)}
                        {selectedPartnerOrgs.length > 5 && <li>...and {selectedPartnerOrgs.length - 5} more</li>}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">EHR System *</label>
                  <select
                    value={externalSystemId}
                    onChange={(e) => setExternalSystemId(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Select EHR system...</option>
                    {ehrSystems.map((sys) => (
                      <option key={sys.slug} value={sys.slug}>
                        {sys.displayName}
                      </option>
                    ))}
                  </select>
                  <p className="mt-2 text-xs text-gray-600">
                    Any org configured with this EHR (and a matching externalTenantId) can be bootstrapped with this one credential.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expires In (days)</label>
                <select
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value={30}>30 days</option>
                  <option value={90}>90 days</option>
                  <option value={180}>180 days</option>
                  <option value={365}>1 year</option>
                </select>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Revoke Confirmation Modal
const RevokeConfirmationModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  serviceAccount: ServiceAccountWithStats | null;
  onRevoked: () => void;
}> = ({ isOpen, onClose, serviceAccount, onRevoked }) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRevoke = async () => {
    if (!serviceAccount) return;
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${API_URL}/api/v1/platform/external-integrations/service-accounts/${serviceAccount.id}/revoke`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ reason: reason || 'Manual revocation by admin' })
        }
      );

      const data = await response.json();
      if (data.success) {
        onRevoked();
        onClose();
        setReason('');
      } else {
        setError(extractApiError(data, 'Failed to revoke service account'));
      }
    } catch (err) {
      setError('Failed to revoke service account');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !serviceAccount) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-red-50 to-orange-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <ShieldExclamationIcon className="w-5 h-5 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Revoke Service Account</h3>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">
              <strong>Warning:</strong> This action is immediate and cannot be undone. All API requests using this service account will be rejected immediately.
            </p>
          </div>

          <div className="text-sm text-gray-600">
            <p>You are about to revoke:</p>
            <p className="font-medium text-gray-900 mt-1">{serviceAccount.name}</p>
            <p className="text-gray-500">{serviceAccount.clientId}</p>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Security concern, expired contract, etc."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleRevoke}
              disabled={loading}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Revoking...' : 'Revoke Now'}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Renew / Reactivate Modal
const RenewServiceAccountModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  serviceAccount: ServiceAccountWithStats | null;
  onRenewed: () => void;
}> = ({ isOpen, onClose, serviceAccount, onRenewed }) => {
  const [durationDays, setDurationDays] = useState(365);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const durationOptions = [
    { label: '30 days', value: 30 },
    { label: '90 days', value: 90 },
    { label: '6 months', value: 180 },
    { label: '1 year', value: 365 },
    { label: '2 years', value: 730 },
    { label: '3 years', value: 1095 },
  ];

  const handleRenew = async () => {
    if (!serviceAccount) return;
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${API_URL}/api/v1/platform/external-integrations/service-accounts/${serviceAccount.id}/renew`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ durationDays })
        }
      );

      const data = await response.json();
      if (data.success) {
        onRenewed();
        onClose();
      } else {
        setError(extractApiError(data, 'Failed to renew service account'));
      }
    } catch (err) {
      setError('Failed to renew service account');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !serviceAccount) return null;

  const isExpired = serviceAccount.expiresAt && new Date(serviceAccount.expiresAt) <= new Date();
  const isRevoked = !serviceAccount.isActive;
  const newExpiry = new Date();
  newExpiry.setDate(newExpiry.getDate() + durationDays);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-green-50 to-emerald-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <ArrowPathIcon className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              {isRevoked ? 'Reactivate' : 'Renew'} Service Account
            </h3>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="text-sm text-gray-600">
            <p className="font-medium text-gray-900">{serviceAccount.name}</p>
            <p className="text-gray-500">{serviceAccount.clientId}</p>
            {isRevoked && (
              <p className="mt-1 text-orange-600 font-medium">This account was revoked and will be reactivated.</p>
            )}
            {isExpired && !isRevoked && (
              <p className="mt-1 text-yellow-600 font-medium">This account expired on {new Date(serviceAccount.expiresAt!).toLocaleDateString()}.</p>
            )}
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Duration</label>
            <div className="grid grid-cols-3 gap-2">
              {durationOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setDurationDays(opt.value)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
                    durationDays === opt.value
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
            New expiration: <span className="font-medium text-gray-900">{newExpiry.toLocaleDateString()}</span>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 min-h-[44px] border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleRenew}
              disabled={loading}
              className="flex-1 px-4 py-2 min-h-[44px] bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Renewing...' : `${isRevoked ? 'Reactivate' : 'Renew'} Now`}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// EHR Create / Edit Modal (Workstream B)
const WebhookManagementModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  webhook: Webhook | null; // null => create mode
  onSaved: () => void;
  onSecret: (name: string, secret: string) => void;
}> = ({ isOpen, onClose, webhook, onSaved, onSecret }) => {
  const isEdit = !!webhook;
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<string[]>(['clinicalResults.available']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    if (webhook) {
      setName(webhook.name);
      setUrl(webhook.url);
      setEvents(webhook.events?.length ? webhook.events : ['clinicalResults.available']);
    } else {
      setName(''); setUrl(''); setEvents(['clinicalResults.available']);
    }
    setError(null);
  }, [webhook, isOpen]);

  const toggleEvent = (ev: string) =>
    setEvents((prev) => (prev.includes(ev) ? prev.filter((e) => e !== ev) : [...prev, ev]));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('accessToken');
      const base = `${API_URL}/api/v1/platform/external-integrations/webhooks`;
      const target = isEdit ? `${base}/${webhook!.id}` : base;
      const response = await fetch(target, {
        method: isEdit ? 'PATCH' : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, url, events }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        if (!isEdit && data.data?.secret) onSecret(data.data.name, data.data.secret);
        onSaved();
        onClose();
      } else {
        setError(data.error || 'Failed to save webhook.');
      }
    } catch {
      setError('Failed to save webhook.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-pink-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <BoltIcon className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{isEdit ? 'Edit webhook' : 'Add webhook'}</h3>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-lg transition-colors">
              <XMarkIcon className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>
        <div className="p-6 overflow-y-auto">
        {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required placeholder="CareTracker Prod" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
            <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} required placeholder="https://ehr.example.com/api/webhook" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Events</label>
            <div className="grid grid-cols-1 gap-1 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2">
              {WEBHOOK_EVENT_OPTIONS.map((ev) => (
                <label key={ev} className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={events.includes(ev)} onChange={() => toggleEvent(ev)} />
                  <span className="font-mono text-xs">{ev}</span>
                </label>
              ))}
            </div>
          </div>
          {!isEdit && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              A signing secret will be generated and shown once after you save.
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={loading || events.length === 0} className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50">{loading ? 'Saving...' : isEdit ? 'Save' : 'Create'}</button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

const WebhookOrgsModal: React.FC<{
  webhook: Webhook;
  organizations: Array<{ id: string; name: string; settings?: { webhookId?: string | null } }>;
  onClose: () => void;
  onSaved: () => void;
}> = ({ webhook, organizations, onClose, onSaved }) => {
  // Fetch the org list fresh on open so pre-selection reflects server truth, not a
  // stale parent cache. The assign endpoint is a replace-set, so a stale starting
  // set could otherwise silently unassign orgs on save.
  const [orgs, setOrgs] = useState(organizations);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const seed = (list: typeof organizations) => {
      const init = new Set<string>();
      list.forEach((o) => { if (o.settings?.webhookId === webhook.id) init.add(o.id); });
      if (!cancelled) { setOrgs(list); setSelected(init); }
    };
    (async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const res = await fetch(`${API_URL}/api/v1/platform/organizations`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        seed(res.ok && data.success ? data.data : organizations);
      } catch {
        seed(organizations);
      }
    })();
    return () => { cancelled = true; };
  }, [webhook.id, organizations]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/v1/platform/external-integrations/webhooks/${webhook.id}/organizations`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ organizationIds: Array.from(selected) }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) onSaved();
      else setError(data.error || 'Failed to update organizations.');
    } catch {
      setError('Failed to update organizations.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Assign organizations</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="w-5 h-5" /></button>
        </div>
        <p className="mt-2 text-sm text-gray-600">Organizations delivering to <span className="font-semibold">{webhook.name}</span>.</p>
        {error && <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>}
        <div className="mt-4 grid grid-cols-1 gap-1 max-h-72 overflow-y-auto border border-gray-200 rounded-lg p-2">
          {orgs.length === 0 && <p className="text-sm text-gray-400 p-2">No organizations.</p>}
          {orgs.map((o) => (
            <label key={o.id} className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={selected.has(o.id)} onChange={() => toggle(o.id)} />
              <span>{o.name}</span>
            </label>
          ))}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} disabled={loading} className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50">{loading ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
};

const EhrManagementModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  ehr: EhrSystem | null; // null => create mode
  webhooks: Webhook[];
  onSaved: () => void;
}> = ({ isOpen, onClose, ehr, webhooks, onSaved }) => {
  const isEdit = !!ehr;

  const [slug, setSlug] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [vendor, setVendor] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [authPath, setAuthPath] = useState('');
  const [healthCheckPath, setHealthCheckPath] = useState('');
  const [defaultWebhookId, setDefaultWebhookId] = useState('');
  const [capabilities, setCapabilities] = useState<Record<string, Record<string, boolean>>>(DEFAULT_CREATE_CAPABILITIES);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Hydrate form whenever the target EHR (or create mode) changes.
  useEffect(() => {
    if (!isOpen) return;
    if (ehr) {
      setSlug(ehr.slug);
      setDisplayName(ehr.displayName ?? '');
      setVendor(ehr.vendor ?? '');
      setIsActive(ehr.isActive);
      setAuthPath(ehr.authPath ?? '');
      setHealthCheckPath(ehr.healthCheckPath ?? '');
      setDefaultWebhookId(ehr.defaultWebhookId ?? '');
      setCapabilities((ehr.capabilities as Record<string, Record<string, boolean>>) ?? {});
    } else {
      setSlug('');
      setDisplayName('');
      setVendor('');
      setIsActive(true);
      setAuthPath('');
      setHealthCheckPath('');
      setDefaultWebhookId('');
      setCapabilities(DEFAULT_CREATE_CAPABILITIES);
    }
    setShowAdvanced(false);
    setError(null);
  }, [ehr, isOpen]);

  const toggleCapability = (resource: string, operation: string) => {
    setCapabilities((prev) => ({
      ...prev,
      [resource]: {
        ...(prev[resource] ?? {}),
        [operation]: !prev[resource]?.[operation]
      }
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const body: Record<string, unknown> = {
      displayName,
      vendor: vendor || undefined,
      authPath: authPath || undefined,
      healthCheckPath: healthCheckPath || undefined,
      defaultWebhookId: defaultWebhookId || null,
      capabilities
    };

    if (!isEdit) {
      body.slug = slug;
    }

    try {
      const token = localStorage.getItem('accessToken');
      const url = isEdit
        ? `${API_URL}/api/v1/platform/external-integrations/ehrs/${ehr!.slug}`
        : `${API_URL}/api/v1/platform/external-integrations/ehrs`;

      const response = await fetch(url, {
        method: isEdit ? 'PATCH' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success) {
        onSaved();
        onClose();
      } else if (response.status === 409) {
        setError(extractApiError(data, 'An EHR with this slug already exists.'));
      } else if (response.status === 400) {
        setError(extractApiError(data, 'Invalid slug. Use a lowercase, URL-safe identifier.'));
      } else {
        setError(extractApiError(data, `Failed to ${isEdit ? 'update' : 'create'} EHR system.`));
      }
    } catch (err) {
      setError(`Failed to ${isEdit ? 'update' : 'create'} EHR system.`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-pink-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <RectangleStackIcon className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {isEdit ? 'Edit EHR System' : 'Add EHR System'}
              </h3>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-lg transition-colors">
              <XMarkIcon className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}

            {/* BASIC fields */}
            {!isEdit && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  required
                  placeholder="prime"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Lowercase, URL-safe identifier. Immutable after creation.
                </p>
              </div>
            )}

            {isEdit && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                <code className="block px-3 py-2 bg-gray-100 rounded-lg text-sm font-mono text-gray-600">
                  {ehr!.slug}
                </code>
                <p className="mt-1 text-xs text-gray-500">Slug cannot be changed.</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Name *</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
                placeholder="Prime Medical Suite"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
              <input
                type="text"
                value={vendor}
                onChange={(e) => setVendor(e.target.value)}
                placeholder="Prime Health, Inc."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default webhook</label>
              <select
                value={defaultWebhookId}
                onChange={(e) => setDefaultWebhookId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">None</option>
                {defaultWebhookId && !webhooks.some((w) => w.id === defaultWebhookId) && (
                  <option value={defaultWebhookId}>Current webhook</option>
                )}
                {webhooks.filter(w => w.isActive || w.id === defaultWebhookId).map(w => (
                  <option key={w.id} value={w.id}>{w.name}{!w.isActive ? ' (inactive)' : ''}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Organizations on this EHR without their own webhook deliver here, and this prefills their settings.
              </p>
            </div>

            {isEdit && (
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <span>Status:</span>
                <span className={isActive ? 'text-emerald-700' : 'text-gray-500'}>
                  {isActive ? 'Active' : 'Inactive'}
                </span>
                <span className="text-xs font-normal text-gray-500">
                  (use Deactivate/Reactivate on the list to change lifecycle)
                </span>
              </div>
            )}

            {/* ADVANCED collapsible section */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowAdvanced((s) => !s)}
                className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <span className="text-sm font-medium text-gray-700">Advanced</span>
                {showAdvanced ? (
                  <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                ) : (
                  <ChevronRightIcon className="w-4 h-4 text-gray-500" />
                )}
              </button>

              {showAdvanced && (
                <div className="p-3 space-y-4 border-t border-gray-200">
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                      <p className="text-sm text-amber-800">
                        These settings affect live integration behavior for every organization on this
                        EHR. Change them only if you understand the downstream impact.
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Auth Path</label>
                    <input
                      type="text"
                      value={authPath}
                      onChange={(e) => setAuthPath(e.target.value)}
                      placeholder="/oauth/token"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Health Check Path</label>
                    <input
                      type="text"
                      value={healthCheckPath}
                      onChange={(e) => setHealthCheckPath(e.target.value)}
                      placeholder="/health"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Capabilities</label>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-gray-500">
                            <th className="py-1 pr-2 font-medium">Resource</th>
                            {EHR_CAPABILITY_OPERATIONS.map((op) => (
                              <th key={op} className="py-1 px-2 font-medium capitalize text-center">{op}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {EHR_CAPABILITY_RESOURCES.map((resource) => (
                            <tr key={resource} className="border-t border-gray-100">
                              <td className="py-1 pr-2 text-gray-700 capitalize">{resource}</td>
                              {EHR_CAPABILITY_OPERATIONS.map((op) => (
                                <td key={op} className="py-1 px-2 text-center">
                                  <input
                                    type="checkbox"
                                    aria-label={`${resource} ${op}`}
                                    checked={!!capabilities[resource]?.[op]}
                                    onChange={() => toggleCapability(resource, op)}
                                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                  />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Saving...' : isEdit ? 'Save Changes' : 'Create EHR'}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Main Component
const ExternalIntegrationsDashboard: React.FC = () => {
  const { user, hasPermission } = useAuth();

  // Data-driven EHR registry (Workstream B): slug->label for the Scope column,
  // and refetched so the EHRs tab and registry stay in sync after edits.
  const { labelFor: ehrLabelFor, refetch: refetchEhrRegistry } = useEhrRegistry();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [granularity, setGranularity] = useState<Granularity>('1h');
  const [selectedOrganization, setSelectedOrganization] = useState<string>('all');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortState, setSortState] = useState<SortState>({ column: 'name', direction: 'asc' });
  const [ehrSortState, setEhrSortState] = useState<SortState>({ column: 'displayName', direction: 'asc' });

  // Data state
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [serviceAccounts, setServiceAccounts] = useState<ServiceAccountWithStats[]>([]);
  // Paged by the server, one status per list.
  const [serviceAccountStatus, setServiceAccountStatus] = useState<ServiceAccountView>('active');
  const [serviceAccountPagination, setServiceAccountPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [expiredServiceAccounts, setExpiredServiceAccounts] = useState<ServiceAccountWithStats[]>([]);
  const [expiredPagination, setExpiredPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [revokedCount, setRevokedCount] = useState<number | null>(null);
  const [trends, setTrends] = useState<IngestionTrendPoint[]>([]);
  const [errorBreakdown, setErrorBreakdown] = useState<ErrorBreakdown[]>([]);
  const [recentFailures, setRecentFailures] = useState<RecentFailure[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ServiceAccountWithStats | null>(null);
  const [renewTarget, setRenewTarget] = useState<ServiceAccountWithStats | null>(null);
  const [expandedFailure, setExpandedFailure] = useState<string | null>(null);

  // EHR management state (Workstream B)
  const [ehrs, setEhrs] = useState<EhrSystem[]>([]);
  // Whether "Add EHR System" is available comes from the backend, which requires both
  // a finished enum-to-FK conversion and an operator opt-in per environment.
  const [ehrCreation, setEhrCreation] = useState<{ enabled: boolean; reason?: string }>({ enabled: false });
  const [ehrsLoaded, setEhrsLoaded] = useState(false);
  const [ehrError, setEhrError] = useState<string | null>(null);
  const [showEhrModal, setShowEhrModal] = useState(false);
  const [editEhrTarget, setEditEhrTarget] = useState<EhrSystem | null>(null);
  const [ehrActionSlug, setEhrActionSlug] = useState<string | null>(null);
  const [deactivateEhrTarget, setDeactivateEhrTarget] = useState<EhrSystem | null>(null);

  // Webhook management state
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [webhooksLoaded, setWebhooksLoaded] = useState(false);
  const [webhookError, setWebhookError] = useState<string | null>(null);
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [editWebhookTarget, setEditWebhookTarget] = useState<Webhook | null>(null);
  const [webhookActionId, setWebhookActionId] = useState<string | null>(null);
  const [assignOrgsTarget, setAssignOrgsTarget] = useState<Webhook | null>(null);
  const [revealedSecret, setRevealedSecret] = useState<{ name: string; secret: string } | null>(null);
  // The tab's own paged list. `webhooks` above stays the full list the EHR dropdown reads.
  const [webhookView, setWebhookView] = useState<WebhookView>('active');
  const [webhookPageRows, setWebhookPageRows] = useState<Webhook[]>([]);
  const [webhookPageLoaded, setWebhookPageLoaded] = useState(false);
  const [webhookPagination, setWebhookPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [webhookViewCounts, setWebhookViewCounts] = useState<Record<WebhookView, number | null>>({ active: null, inactive: null });
  const [deleteWebhookTarget, setDeleteWebhookTarget] = useState<Webhook | null>(null);

  // Polling ref
  const abortControllerRef = useRef<AbortController | null>(null);

  // Check permissions
  const isSuperadmin = user?.role === 'superadmin';
  const hasIntegrationsPermission = hasPermission('platform.external_integrations.manage');

  // Sorted service accounts
  const sortedServiceAccounts = useMemo(() => {
    if (!sortState.column || !sortState.direction) return serviceAccounts;

    const getValue = (obj: any, path: string) =>
      path.split('.').reduce((acc, part) => acc?.[part], obj);

    return [...serviceAccounts].sort((a, b) => {
      const aVal = getValue(a, sortState.column!);
      const bVal = getValue(b, sortState.column!);
      if (aVal == null) return sortState.direction === 'asc' ? 1 : -1;
      if (bVal == null) return sortState.direction === 'asc' ? -1 : 1;
      const cmp = typeof aVal === 'string'
        ? aVal.localeCompare(bVal)
        : Number(aVal) - Number(bVal);
      return sortState.direction === 'asc' ? cmp : -cmp;
    });
  }, [serviceAccounts, sortState]);

  // DataTable column definitions for Service Accounts
  const serviceAccountColumns: ColumnDefinition<ServiceAccountWithStats>[] = useMemo(() => [
    {
      id: 'name',
      header: 'Name',
      sortable: true,
      sortKey: 'name',
      accessor: (sa) => (
        <div>
          <p className="font-medium text-gray-900">{sa.name}</p>
          <p className="text-xs text-gray-500 font-mono">{sa.clientId}</p>
        </div>
      )
    },
    {
      id: 'organization',
      header: 'Scope',
      sortable: true,
      sortKey: 'organizationName',
      accessor: (sa) => (
        <div className="flex items-center gap-2">
          <BuildingOfficeIcon className="w-4 h-4 text-gray-400" />
          {sa.externalSystemId ? (
            <span className="text-gray-700">
              {ehrLabelFor(sa.externalSystemId)}
              <span className="ml-1 text-xs text-purple-600">(all orgs on EHR)</span>
            </span>
          ) : sa.partnerId ? (
            <span className="text-gray-700">
              {sa.partnerName || 'Partner'}
              <span className="ml-1 text-xs text-purple-600">(all orgs)</span>
            </span>
          ) : (
            <span className="text-gray-700">{sa.organizationName || 'Unknown'}</span>
          )}
        </div>
      )
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      sortKey: 'isActive',
      accessor: (sa) => {
        const isExpired = sa.expiresAt && new Date(sa.expiresAt) < new Date();
        return <StatusBadge active={sa.isActive} expired={isExpired} />;
      }
    },
    {
      id: 'ingestions',
      header: 'Ingestions (24h)',
      sortable: true,
      sortKey: 'stats.totalIngestions24h',
      align: 'right',
      accessor: (sa) => (
        <span className="font-medium text-gray-900">
          {formatNumber(sa.stats?.totalIngestions24h)}
        </span>
      )
    },
    {
      id: 'successRate',
      header: 'Success Rate',
      sortable: true,
      sortKey: 'stats.successRate24h',
      align: 'right',
      accessor: (sa) => (
        <span className={`font-medium ${
          (sa.stats?.successRate24h ?? 0) >= 95 ? 'text-emerald-600' :
          (sa.stats?.successRate24h ?? 0) >= 80 ? 'text-amber-600' : 'text-red-600'
        }`}>
          {formatPercent(sa.stats?.successRate24h)}
        </span>
      )
    },
    {
      id: 'lastUsed',
      header: 'Last Used',
      sortable: true,
      sortKey: 'lastUsedAt',
      align: 'right',
      accessor: (sa) => (
        <span className="text-gray-500">{formatRelativeTime(sa.lastUsedAt)}</span>
      )
    }
  ], [ehrLabelFor]);

  // DataTable action definitions for Service Accounts
  const serviceAccountActions: ActionDefinition<ServiceAccountWithStats>[] = useMemo(() => [
    {
      id: 'renew',
      icon: <ArrowPathIcon className="w-4 h-4" />,
      tooltip: 'Renew',
      onClick: (sa) => setRenewTarget(sa),
      show: (sa) => !sa.isActive || (!!sa.expiresAt && new Date(sa.expiresAt) <= new Date()),
      variant: 'default'
    },
    {
      id: 'revoke',
      icon: <TrashIcon className="w-4 h-4" />,
      tooltip: 'Revoke',
      onClick: (sa) => setRevokeTarget(sa),
      show: (sa) => sa.isActive,
      variant: 'danger'
    }
  ], []);

  // Sorted EHR systems (client-side, column-driven)
  const sortedEhrs = useMemo(() => {
    if (!ehrSortState.column || !ehrSortState.direction) return ehrs;
    const getValue = (obj: any, path: string) =>
      path.split('.').reduce((acc, part) => acc?.[part], obj);
    return [...ehrs].sort((a, b) => {
      const aVal = getValue(a, ehrSortState.column!);
      const bVal = getValue(b, ehrSortState.column!);
      if (aVal == null) return ehrSortState.direction === 'asc' ? 1 : -1;
      if (bVal == null) return ehrSortState.direction === 'asc' ? -1 : 1;
      const cmp = typeof aVal === 'string'
        ? aVal.localeCompare(bVal)
        : Number(aVal) - Number(bVal);
      return ehrSortState.direction === 'asc' ? cmp : -cmp;
    });
  }, [ehrs, ehrSortState]);

  // DataTable column definitions for EHR systems
  const ehrColumns: ColumnDefinition<EhrSystem>[] = useMemo(() => [
    {
      id: 'name',
      header: 'Name',
      sortable: true,
      sortKey: 'displayName',
      isPrimary: true,
      accessor: (ehr) => (
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl shadow-lg shrink-0">
            <RectangleStackIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-medium text-gray-900">{ehr.displayName}</p>
            <p className="text-xs text-gray-500 font-mono">{ehr.slug}</p>
            {ehr.vendor && (
              <p className="text-xs text-gray-400 mt-0.5">
                <span className="font-medium text-gray-500">Vendor:</span> {ehr.vendor}
              </p>
            )}
          </div>
        </div>
      )
    },
    {
      id: 'defaultWebhook',
      header: 'Default Webhook',
      sortable: true,
      sortKey: 'defaultWebhookId',
      accessor: (ehr) => {
        if (!ehr.defaultWebhookId) return <span className="text-xs text-gray-400">—</span>;
        const wh = webhooks.find((w) => w.id === ehr.defaultWebhookId);
        return (
          <span className="inline-flex items-center gap-1 text-sm text-gray-700">
            <BoltIcon className="w-3.5 h-3.5 text-purple-500" />
            {wh ? wh.name : 'Unknown webhook'}
            {wh && !wh.isActive ? <span className="text-xs text-red-600">(inactive)</span> : null}
          </span>
        );
      }
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      sortKey: 'isActive',
      accessor: (ehr) => ehr.isActive ? (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 ring-1 ring-emerald-500/20">
          <CheckCircleIcon className="w-3 h-3" />
          Active
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 ring-1 ring-gray-500/20">
          <XCircleIcon className="w-3 h-3" />
          Inactive
        </span>
      )
    },
    {
      id: 'connector',
      header: 'Connector',
      sortable: true,
      sortKey: 'connectorStatus',
      accessor: (ehr) => (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ring-1 ${
          ehr.connectorStatus === 'ready'
            ? 'bg-purple-100 text-purple-700 ring-purple-500/20'
            : 'bg-gray-100 text-gray-600 ring-gray-500/20'
        }`}>
          <ServerStackIcon className="w-3 h-3" />
          {ehr.connectorStatus === 'ready' ? 'Ready' : 'None'}
        </span>
      )
    }
  ], [webhooks]);

  // Fetch organizations
  useEffect(() => {
    if (!isSuperadmin || !hasIntegrationsPermission) return;

    const fetchOrganizations = async () => {
      const token = localStorage.getItem('accessToken');
      if (!token) return;

      try {
        const response = await fetch(`${API_URL}/api/v1/platform/organizations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setOrganizations(data.data);
          }
        }
      } catch (err) {
        console.error('Failed to fetch organizations:', err);
      }
    };

    fetchOrganizations();

    const fetchPartners = async () => {
      const token = localStorage.getItem('accessToken');
      if (!token) return;
      try {
        const response = await fetch(`${API_URL}/api/v1/platform/external-integrations/partners`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) setPartners(data.data);
        }
      } catch (err) {
        console.error('Failed to fetch partners:', err);
      }
    };
    fetchPartners();
  }, [isSuperadmin, hasIntegrationsPermission]);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      const orgParam = selectedOrganization !== 'all' ? `&organizationId=${selectedOrganization}` : '';

      const response = await fetch(
        `${API_URL}/api/v1/platform/external-integrations/dashboard?granularity=${granularity}${orgParam}`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          signal: abortControllerRef.current.signal
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSummary(data.data.summary);
          setTrends(data.data.trends || []);
          setAlerts(data.data.alerts || []);
        }
      } else {
        setError('Failed to load dashboard data');
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [granularity, selectedOrganization]);

  // Fetch one page of service accounts in the selected status, plus each status's total for the
  // tab labels. The endpoint pages (20 by default); reading only `data` used to show the newest
  // 20 of every status and silently drop the rest.
  const serviceAccountRequest = useRef(0);
  const fetchServiceAccounts = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    // Switching view or page can start overlapping requests; only the latest may write.
    const request = ++serviceAccountRequest.current;

    const query = (status: ServiceAccountListStatus, page: number, limit: number) => {
      const params = new URLSearchParams({ status, page: String(page), limit: String(limit) });
      if (selectedOrganization !== 'all') params.set('organizationId', selectedOrganization);
      if (searchQuery) params.set('search', searchQuery);
      return fetch(`${API_URL}/api/v1/platform/external-integrations/service-accounts?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
    };

    const read = async (r: Response) => {
      if (!r.ok) return null;
      const data = await r.json();
      return data.success ? { rows: (data.data || []) as ServiceAccountWithStats[], total: (data.total ?? 0) as number } : null;
    };

    try {
      const [list, expired, revoked] = await Promise.all([
        query(serviceAccountStatus, serviceAccountPagination.page, serviceAccountPagination.limit).then(read),
        query('expired', expiredPagination.page, expiredPagination.limit).then(read),
        query('revoked', 1, 1).then(read),
      ]);
      if (request !== serviceAccountRequest.current) return;
      if (list) {
        setServiceAccounts(list.rows);
        setServiceAccountPagination(prev => ({ ...prev, total: list.total }));
      }
      if (expired) {
        setExpiredServiceAccounts(expired.rows);
        setExpiredPagination(prev => ({ ...prev, total: expired.total }));
      }
      setRevokedCount(revoked ? revoked.total : null);
    } catch (err) {
      console.error('Error fetching service accounts:', err);
    }
  }, [selectedOrganization, searchQuery, serviceAccountStatus, serviceAccountPagination.page, serviceAccountPagination.limit, expiredPagination.page, expiredPagination.limit]);

  // A new view, search or organization starts from the first page.
  useEffect(() => {
    setServiceAccountPagination(prev => (prev.page === 1 ? prev : { ...prev, page: 1 }));
    setExpiredPagination(prev => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  }, [serviceAccountStatus, searchQuery, selectedOrganization]);

  // Fetch ingestion stats
  const fetchIngestionStats = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    try {
      const orgParam = selectedOrganization !== 'all' ? `&organizationId=${selectedOrganization}` : '';

      const [statsRes, failuresRes] = await Promise.all([
        fetch(`${API_URL}/api/v1/platform/external-integrations/ingestion/stats?granularity=${granularity}${orgParam}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/v1/platform/external-integrations/ingestion/failures?limit=10${orgParam}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (statsRes.ok) {
        const data = await statsRes.json();
        if (data.success) {
          setTrends(data.data.trends || []);
          setErrorBreakdown(data.data.errorBreakdown || []);
        }
      }

      if (failuresRes.ok) {
        const data = await failuresRes.json();
        if (data.success) {
          setRecentFailures(data.data || []);
        }
      }
    } catch (err) {
      console.error('Error fetching ingestion stats:', err);
    }
  }, [granularity, selectedOrganization]);

  // Fetch EHR registry (platform management view, includes inactive systems)
  const fetchEhrs = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    setEhrError(null);
    try {
      const response = await fetch(
        `${API_URL}/api/v1/platform/external-integrations/ehrs`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        setEhrs(data.data || []);
        setEhrCreation({
          enabled: data.meta?.creationEnabled === true,
          reason: data.meta?.creationDisabledReason,
        });
      } else {
        setEhrError(data.error || 'Failed to load EHR systems');
      }
    } catch (err) {
      console.error('Error fetching EHR systems:', err);
      setEhrError('Failed to load EHR systems');
    } finally {
      setEhrsLoaded(true);
    }
  }, []);

  // Deactivate / Reactivate an EHR system. Surfaces 409 (dependent service
  // accounts) and any other server error to the user rather than swallowing it.
  const handleEhrLifecycle = useCallback(async (slug: string, action: 'deactivate' | 'reactivate') => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    setEhrActionSlug(slug);
    setEhrError(null);
    try {
      const response = await fetch(
        `${API_URL}/api/v1/platform/external-integrations/ehrs/${slug}/${action}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        await fetchEhrs();
        refetchEhrRegistry();
      } else {
        setEhrError(data.error || `Failed to ${action} EHR system.`);
      }
    } catch (err) {
      setEhrError(`Failed to ${action} EHR system.`);
    } finally {
      setEhrActionSlug(null);
    }
  }, [fetchEhrs, refetchEhrRegistry]);

  // ----- Webhooks -----
  const fetchWebhooks = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/v1/platform/external-integrations/webhooks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        setWebhooks(data.data || []);
      } else {
        setWebhookError(data.error || 'Failed to load webhooks.');
      }
    } catch {
      setWebhookError('Failed to load webhooks.');
    } finally {
      setWebhooksLoaded(true);
    }
  }, []);

  // One page of the selected view, plus the other view's total for its tab label.
  const webhookPageRequest = useRef(0);
  const fetchWebhookPage = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    // Switching view or page can start overlapping requests; only the latest may write.
    const request = ++webhookPageRequest.current;
    const query = async (status: WebhookView, page: number, limit: number) => {
      const params = new URLSearchParams({ status, page: String(page), limit: String(limit) });
      const response = await fetch(`${API_URL}/api/v1/platform/external-integrations/webhooks?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) throw new Error(data.error || 'Failed to load webhooks.');
      return { rows: (data.data || []) as Webhook[], total: (data.total ?? 0) as number };
    };
    const other: WebhookView = webhookView === 'active' ? 'inactive' : 'active';
    try {
      const [list, otherList] = await Promise.all([
        query(webhookView, webhookPagination.page, webhookPagination.limit),
        query(other, 1, 1),
      ]);
      if (request !== webhookPageRequest.current) return;
      // The page can fall past the end after a delete, a status change or another admin's edit.
      // Move to the new last page; the page change refetches.
      const lastPage = Math.max(1, Math.ceil(list.total / webhookPagination.limit));
      if (list.rows.length === 0 && webhookPagination.page > lastPage) {
        setWebhookPagination(prev => ({ ...prev, page: lastPage, total: list.total }));
        return;
      }
      setWebhookPageRows(list.rows);
      setWebhookPagination(prev => ({ ...prev, total: list.total }));
      setWebhookViewCounts({ [webhookView]: list.total, [other]: otherList.total } as Record<WebhookView, number>);
    } catch (err) {
      if (request !== webhookPageRequest.current) return;
      setWebhookError(err instanceof Error ? err.message : 'Failed to load webhooks.');
    } finally {
      if (request === webhookPageRequest.current) setWebhookPageLoaded(true);
    }
  }, [webhookView, webhookPagination.page, webhookPagination.limit]);

  // A change to any webhook refreshes the tab's page and, if loaded, the full list the EHR tab uses.
  const refreshWebhooks = useCallback(async () => {
    await Promise.all([fetchWebhookPage(), webhooksLoaded ? fetchWebhooks() : Promise.resolve()]);
  }, [fetchWebhookPage, fetchWebhooks, webhooksLoaded]);

  const handleWebhookDelete = useCallback(async (w: Webhook) => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    setWebhookActionId(w.id);
    setWebhookError(null);
    try {
      const response = await fetch(`${API_URL}/api/v1/platform/external-integrations/webhooks/${w.id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        await refreshWebhooks();
      } else {
        setWebhookError(data.message || data.error || 'Failed to delete webhook.');
      }
    } catch { setWebhookError('Failed to delete webhook.'); }
    finally { setWebhookActionId(null); }
  }, [refreshWebhooks]);

  const handleWebhookLifecycle = useCallback(async (id: string, action: 'deactivate' | 'reactivate') => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    setWebhookActionId(id);
    setWebhookError(null);
    try {
      const response = await fetch(`${API_URL}/api/v1/platform/external-integrations/webhooks/${id}/${action}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) { await refreshWebhooks(); }
      else { setWebhookError(data.error || `Failed to ${action} webhook.`); }
    } catch { setWebhookError(`Failed to ${action} webhook.`); }
    finally { setWebhookActionId(null); }
  }, [refreshWebhooks]);

  const webhookColumns: ColumnDefinition<Webhook>[] = useMemo(() => [
    {
      id: 'name', header: 'Name', sortable: true, sortKey: 'name', isPrimary: true,
      accessor: (w) => (
        <div>
          <p className="font-medium text-gray-900">{w.name}</p>
          <p className="text-xs text-gray-500 break-all">{w.url}</p>
        </div>
      )
    },
    {
      id: 'events', header: 'Events', sortable: false,
      accessor: (w) => <span className="text-xs text-gray-600">{w.events.join(', ')}</span>
    },
    {
      id: 'status', header: 'Status', sortable: true, sortKey: 'isActive',
      accessor: (w) => w.isActive ? (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 ring-1 ring-emerald-500/20"><CheckCircleIcon className="w-3 h-3" />Active</span>
      ) : (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 ring-1 ring-gray-500/20"><XCircleIcon className="w-3 h-3" />Inactive</span>
      )
    },
    {
      id: 'orgs', header: 'Orgs', sortable: true, sortKey: 'assignedOrganizationCount', align: 'right',
      accessor: (w) => <span className="font-medium text-gray-900">{w.assignedOrganizationCount}</span>
    },
    {
      id: 'deliveries', header: 'Deliveries', sortable: true, sortKey: 'totalDeliveries', align: 'right',
      accessor: (w) => (
        <span className="text-gray-600">
          <span className="text-emerald-600">{w.successfulDeliveries}</span>
          {' / '}
          <span className="text-red-600">{w.failedDeliveries}</span>
        </span>
      )
    }
  ], []);

  const webhookActions: ActionDefinition<Webhook>[] = useMemo(() => [
    {
      id: 'edit', icon: <PencilSquareIcon className="w-4 h-4" />, tooltip: 'Edit',
      onClick: (w) => { setEditWebhookTarget(w); setShowWebhookModal(true); }, variant: 'default'
    },
    {
      id: 'orgs', icon: <BuildingOfficeIcon className="w-4 h-4" />, tooltip: 'Assign organizations',
      onClick: (w) => setAssignOrgsTarget(w), variant: 'default'
    },
    {
      id: 'deactivate', icon: <PowerIcon className="w-4 h-4" />, tooltip: 'Deactivate',
      onClick: (w) => handleWebhookLifecycle(w.id, 'deactivate'),
      show: (w) => w.isActive, disabled: (w) => webhookActionId === w.id, variant: 'danger'
    },
    {
      id: 'reactivate', icon: <PowerIcon className="w-4 h-4" />, tooltip: 'Reactivate',
      onClick: (w) => handleWebhookLifecycle(w.id, 'reactivate'),
      show: (w) => !w.isActive, disabled: (w) => webhookActionId === w.id, variant: 'success'
    },
    {
      id: 'delete', icon: <TrashIcon className="w-4 h-4" />, tooltip: 'Delete',
      onClick: (w) => setDeleteWebhookTarget(w),
      show: isDeletableWebhook, disabled: (w) => webhookActionId === w.id, variant: 'danger'
    }
  ], [handleWebhookLifecycle, webhookActionId]);

  // DataTable action definitions for EHR systems
  const ehrActions: ActionDefinition<EhrSystem>[] = useMemo(() => [
    {
      id: 'edit',
      icon: <PencilSquareIcon className="w-4 h-4" />,
      tooltip: 'Edit',
      onClick: (ehr) => { setEditEhrTarget(ehr); setShowEhrModal(true); },
      variant: 'default'
    },
    {
      id: 'deactivate',
      icon: <PowerIcon className="w-4 h-4" />,
      tooltip: 'Deactivate',
      onClick: (ehr) => setDeactivateEhrTarget(ehr),
      show: (ehr) => ehr.isActive,
      disabled: (ehr) => ehrActionSlug === ehr.slug,
      variant: 'danger'
    },
    {
      id: 'reactivate',
      icon: <PowerIcon className="w-4 h-4" />,
      tooltip: 'Reactivate',
      onClick: (ehr) => handleEhrLifecycle(ehr.slug, 'reactivate'),
      show: (ehr) => !ehr.isActive,
      disabled: (ehr) => ehrActionSlug === ehr.slug,
      variant: 'success'
    }
  ], [ehrActionSlug, handleEhrLifecycle]);

  // Initial fetch
  useEffect(() => {
    if (!isSuperadmin || !hasIntegrationsPermission) {
      setLoading(false);
      return;
    }

    fetchDashboardData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [isSuperadmin, hasIntegrationsPermission, fetchDashboardData]);

  // Tab-specific fetches
  useEffect(() => {
    if (!isSuperadmin || !hasIntegrationsPermission) return;

    if (activeTab === 'service-accounts') {
      fetchServiceAccounts();
    } else if (activeTab === 'ingestion') {
      fetchIngestionStats();
    } else if (activeTab === 'ehrs') {
      if (!ehrsLoaded) fetchEhrs();
      if (!webhooksLoaded) fetchWebhooks(); // needed for the EHR default-webhook dropdown
    } else if (activeTab === 'webhooks') {
      fetchWebhookPage();
    }
  }, [activeTab, isSuperadmin, hasIntegrationsPermission, fetchServiceAccounts, fetchIngestionStats, fetchEhrs, ehrsLoaded, fetchWebhooks, webhooksLoaded, fetchWebhookPage]);

  // A new webhook view starts from the first page, with nothing from the previous view on screen.
  useEffect(() => {
    setWebhookPageRows([]);
    setWebhookPageLoaded(false);
    setWebhookError(null);
    setWebhookPagination(prev => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  }, [webhookView]);

  // Polling for real-time updates
  useEffect(() => {
    if (!isSuperadmin || !hasIntegrationsPermission) return;

    let interval: number;

    // Poll based on granularity
    if (granularity === '10s') {
      interval = 10000; // 10 seconds
    } else if (granularity === '1m') {
      interval = 30000; // 30 seconds
    } else {
      interval = 60000; // 1 minute
    }

    const pollId = setInterval(() => {
      if (activeTab === 'overview') {
        fetchDashboardData();
      } else if (activeTab === 'ingestion') {
        fetchIngestionStats();
      }
    }, interval);

    return () => clearInterval(pollId);
  }, [activeTab, granularity, isSuperadmin, hasIntegrationsPermission, fetchDashboardData, fetchIngestionStats]);

  // Handle alert acknowledgment
  const handleAcknowledgeAlert = async (alertId: string) => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    try {
      const response = await fetch(
        `${API_URL}/api/v1/platform/external-integrations/alerts/${alertId}/acknowledge`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        setAlerts(prev => prev.map(a =>
          a.id === alertId ? { ...a, acknowledged: true } : a
        ));
      }
    } catch (err) {
      console.error('Error acknowledging alert:', err);
    }
  };

  // Access control
  if (!isSuperadmin) {
    return <AccessDenied reason="role" />;
  }

  if (!hasIntegrationsPermission) {
    return <AccessDenied reason="permission" />;
  }

  // Loading state
  if (loading) {
    return (
      <PageTransition>
        <div className="flex items-center justify-center h-64">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          >
            <div className="h-12 w-12 border-4 border-purple-600 border-t-transparent rounded-full" />
          </motion.div>
        </div>
      </PageTransition>
    );
  }

  // Error state
  if (error) {
    return (
      <PageTransition>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <ExclamationTriangleIcon className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <p className="text-gray-600">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </PageTransition>
    );
  }

  const unacknowledgedAlerts = alerts.filter(a => !a.acknowledged);

  return (
    <PageTransition className="space-y-8" stagger staggerDelay={0.03}>
      {/* Header */}
      <PageTransitionItem>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl shadow-lg">
                <CloudArrowUpIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">External Integrations</h1>
                <p className="text-gray-500 text-sm">Monitor service accounts and encounter ingestion</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Organization Filter */}
            <select
              value={selectedOrganization}
              onChange={(e) => setSelectedOrganization(e.target.value)}
              className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Organizations</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>

            {/* Granularity Selector */}
            <GranularitySelector value={granularity} onChange={setGranularity} />

            {/* Refresh Button */}
            <button
              onClick={() => fetchDashboardData()}
              className="p-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
            >
              <ArrowPathIcon className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
      </PageTransitionItem>

      {/* Active Alerts Banner */}
      {unacknowledgedAlerts.length > 0 && (
        <PageTransitionItem>
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-red-50 to-amber-50 border border-red-200 rounded-2xl p-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <BellAlertIcon className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    {unacknowledgedAlerts.length} Active Alert{unacknowledgedAlerts.length > 1 ? 's' : ''}
                  </p>
                  <p className="text-sm text-gray-600">
                    {unacknowledgedAlerts.filter(a => a.severity === 'critical').length} critical, {' '}
                    {unacknowledgedAlerts.filter(a => a.severity === 'warning').length} warnings
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {unacknowledgedAlerts.slice(0, 3).map((alert) => (
                  <AlertBadge key={alert.id} severity={alert.severity} />
                ))}
              </div>
            </div>
          </motion.div>
        </PageTransitionItem>
      )}

      {/* Tab Navigation */}
      <PageTransitionItem>
        <div className="flex items-center justify-center">
          <div className="inline-flex bg-white/80 backdrop-blur-sm rounded-xl p-1 border border-white/50 shadow-lg">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                activeTab === 'overview'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-purple-600 hover:bg-white/50'
              }`}
            >
              <ChartBarIcon className="w-4 h-4" />
              Overview
            </button>
            <button
              onClick={() => setActiveTab('ehrs')}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                activeTab === 'ehrs'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-purple-600 hover:bg-white/50'
              }`}
            >
              <RectangleStackIcon className="w-4 h-4" />
              EHRs
            </button>
            <button
              onClick={() => setActiveTab('service-accounts')}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                activeTab === 'service-accounts'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-purple-600 hover:bg-white/50'
              }`}
            >
              <ServerStackIcon className="w-4 h-4" />
              Service Accounts
            </button>
            <button
              onClick={() => setActiveTab('webhooks')}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                activeTab === 'webhooks'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-purple-600 hover:bg-white/50'
              }`}
            >
              <BoltIcon className="w-4 h-4" />
              Webhooks
            </button>
            <button
              onClick={() => setActiveTab('ingestion')}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                activeTab === 'ingestion'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-purple-600 hover:bg-white/50'
              }`}
            >
              <CloudArrowUpIcon className="w-4 h-4" />
              Ingestion Metrics
            </button>
          </div>
        </div>
      </PageTransitionItem>

      {/* ==================== OVERVIEW TAB ==================== */}
      {activeTab === 'overview' && (
        <>
          {/* Summary Cards */}
          <PageTransitionItem>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Active Service Accounts"
                value={formatNumber(summary?.activeServiceAccounts)}
                subtitle={`${formatNumber(summary?.totalServiceAccounts)} total`}
                icon={ServerStackIcon}
                color="purple"
                delay={0}
              />
              <StatCard
                title="Ingestions (24h)"
                value={formatNumber(summary?.totalIngestions24h)}
                subtitle={`${formatNumber(summary?.ingestionsAllTime)} all time`}
                icon={CloudArrowUpIcon}
                color="pink"
                delay={0.05}
              />
              <StatCard
                title="Success Rate"
                value={formatPercent(summary?.successRate24h)}
                subtitle={`${formatPercent(summary?.errorRate24h)} errors`}
                icon={CheckCircleIcon}
                color={summary?.successRate24h && summary.successRate24h >= 95 ? 'emerald' : 'amber'}
                delay={0.1}
              />
              <StatCard
                title="Active Alerts"
                value={formatNumber(unacknowledgedAlerts.length)}
                subtitle={`${alerts.filter(a => a.severity === 'critical').length} critical`}
                icon={BellAlertIcon}
                color={unacknowledgedAlerts.length > 0 ? 'red' : 'emerald'}
                delay={0.15}
              />
            </div>
          </PageTransitionItem>

          {/* Trend Chart + Alerts */}
          <PageTransitionItem>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Ingestion Trend */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/50 shadow-lg"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <ArrowTrendingUpIcon className="w-5 h-5 text-purple-600" />
                    <h2 className="font-semibold text-gray-900">Ingestion Trend</h2>
                  </div>
                </div>
                <div className="pb-6">
                  <TrendChart data={trends} height={200} />
                </div>
              </motion.div>

              {/* Recent Alerts */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/50 shadow-lg"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <BellAlertIcon className="w-5 h-5 text-purple-600" />
                    <h2 className="font-semibold text-gray-900">Recent Alerts</h2>
                  </div>
                </div>

                {alerts.length > 0 ? (
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {alerts.slice(0, 5).map((alert, index) => (
                      <motion.div
                        key={alert.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className={`p-3 rounded-lg border ${
                          alert.acknowledged
                            ? 'bg-gray-50 border-gray-200'
                            : alert.severity === 'critical'
                              ? 'bg-red-50 border-red-200'
                              : 'bg-amber-50 border-amber-200'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <AlertBadge severity={alert.severity} />
                              <span className="text-xs text-gray-500">{formatRelativeTime(alert.timestamp)}</span>
                            </div>
                            <p className="font-medium text-gray-900 mt-1">{alert.title}</p>
                            <p className="text-sm text-gray-600 mt-0.5">{alert.message}</p>
                          </div>
                          {!alert.acknowledged && (
                            <button
                              onClick={() => handleAcknowledgeAlert(alert.id)}
                              className="p-1.5 hover:bg-white rounded-lg transition-colors"
                              title="Acknowledge"
                            >
                              <CheckIcon className="w-4 h-4 text-gray-500" />
                            </button>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-[200px] text-gray-400">
                    <CheckCircleIcon className="w-12 h-12 mb-2" />
                    <p>No active alerts</p>
                  </div>
                )}
              </motion.div>
            </div>
          </PageTransitionItem>
        </>
      )}

      {/* ==================== SERVICE ACCOUNTS TAB ==================== */}
      {activeTab === 'service-accounts' && (
        <>
          {/* Actions Bar */}
          <PageTransitionItem>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="relative flex-1 max-w-md">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search service accounts..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl text-sm font-medium hover:bg-purple-700 transition-colors shadow-lg shadow-purple-500/25"
              >
                <PlusIcon className="w-4 h-4" />
                Create Service Account
              </button>
            </div>
          </PageTransitionItem>

          {/* Views: live accounts (with expired ones first, as attention items) and revoked ones */}
          <PageTransitionItem>
            <div role="tablist" aria-label="Service account status" className="flex gap-2 border-b border-gray-200">
              {SERVICE_ACCOUNT_VIEWS.map(view => {
                const selected = serviceAccountStatus === view;
                const count = view === 'active'
                  ? (serviceAccountStatus === 'active' ? serviceAccountPagination.total + expiredPagination.total : null)
                  : revokedCount;
                return (
                  <button
                    key={view}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    onClick={() => setServiceAccountStatus(view)}
                    className={`-mb-px px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      selected ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {SERVICE_ACCOUNT_VIEW_LABELS[view]}
                    {count !== null && <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{count}</span>}
                  </button>
                );
              })}
            </div>
          </PageTransitionItem>

          {/* Expired: live but unable to get tokens. Someone decides: renew or revoke. */}
          {serviceAccountStatus === 'active' && expiredPagination.total > 0 && (
            <PageTransitionItem>
              <section aria-labelledby="sa-attention-heading" className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <h3 id="sa-attention-heading" className="text-sm font-semibold text-amber-900">
                  Needs attention: {expiredPagination.total} expired service account{expiredPagination.total === 1 ? '' : 's'}
                </h3>
                <p className="mt-1 mb-3 text-sm text-amber-800">
                  These credentials can no longer get tokens. Renew the ones still in use; revoke the rest.
                </p>
                <DataTable
                  data={expiredServiceAccounts}
                  columns={serviceAccountColumns}
                  actions={serviceAccountActions}
                  loading={false}
                  emptyMessage="No expired service accounts"
                  hoverable
                  striped={false}
                  ariaLabel="Expired service accounts"
                  pagination={expiredPagination}
                  onPageChange={(page) => setExpiredPagination(prev => ({ ...prev, page }))}
                  onLimitChange={(limit) => setExpiredPagination(prev => ({ ...prev, limit, page: 1 }))}
                />
              </section>
            </PageTransitionItem>
          )}

          {/* Service Accounts Table */}
          <PageTransitionItem>
            <DataTable
              data={sortedServiceAccounts}
              columns={serviceAccountColumns}
              actions={serviceAccountActions}
              loading={false}
              emptyMessage={`No ${SERVICE_ACCOUNT_VIEW_LABELS[serviceAccountStatus].toLowerCase()} service accounts`}
              emptyIcon={<ServerStackIcon className="h-12 w-12 text-gray-300" />}
              sortable={true}
              sortState={sortState}
              onSortChange={setSortState}
              pagination={serviceAccountPagination}
              onPageChange={(page) => setServiceAccountPagination(prev => ({ ...prev, page }))}
              onLimitChange={(limit) => setServiceAccountPagination(prev => ({ ...prev, limit, page: 1 }))}
              hoverable
              striped={false}
              ariaLabel="Service accounts table"
            />
          </PageTransitionItem>
        </>
      )}

      {/* ==================== INGESTION METRICS TAB ==================== */}
      {activeTab === 'ingestion' && (
        <>
          {/* Stats Cards */}
          <PageTransitionItem>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Total Requests"
                value={formatNumber(trends.reduce((sum, t) => sum + t.total, 0))}
                icon={CloudArrowUpIcon}
                color="purple"
                delay={0}
              />
              <StatCard
                title="Success Rate"
                value={formatPercent(
                  trends.length > 0
                    ? (trends.reduce((sum, t) => sum + t.success, 0) /
                       Math.max(trends.reduce((sum, t) => sum + t.total, 0), 1)) * 100
                    : 0
                )}
                icon={CheckCircleIcon}
                color="emerald"
                delay={0.05}
              />
              <StatCard
                title="Failed Requests"
                value={formatNumber(trends.reduce((sum, t) => sum + t.failure, 0))}
                icon={XCircleIcon}
                color="red"
                delay={0.1}
              />
              <StatCard
                title="Avg Latency"
                value={formatLatency(summary?.avgLatencyMs)}
                icon={ClockIcon}
                color="amber"
                delay={0.15}
              />
            </div>
          </PageTransitionItem>

          {/* Charts */}
          <PageTransitionItem>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Trend Chart */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/50 shadow-lg"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <ArrowTrendingUpIcon className="w-5 h-5 text-purple-600" />
                    <h2 className="font-semibold text-gray-900">Success/Failure Trend</h2>
                  </div>
                </div>
                <div className="pb-6">
                  <TrendChart data={trends} height={200} />
                </div>
              </motion.div>

              {/* Error Breakdown */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/50 shadow-lg"
              >
                <div className="flex items-center gap-2 mb-6">
                  <ExclamationTriangleIcon className="w-5 h-5 text-purple-600" />
                  <h2 className="font-semibold text-gray-900">Error Breakdown</h2>
                </div>
                <ErrorBreakdownChart data={errorBreakdown} />
              </motion.div>
            </div>
          </PageTransitionItem>

          {/* Recent Failures Table */}
          <PageTransitionItem>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/50 shadow-lg"
            >
              <div className="flex items-center gap-2 mb-4">
                <XCircleIcon className="w-5 h-5 text-purple-600" />
                <h2 className="font-semibold text-gray-900">Recent Failures</h2>
              </div>

              {recentFailures.length > 0 ? (
                <div className="space-y-3">
                  {recentFailures.map((failure, index) => (
                    <motion.div
                      key={failure.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.03 }}
                      className="border border-gray-200 rounded-lg overflow-hidden"
                    >
                      <div
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                        onClick={() => setExpandedFailure(expandedFailure === failure.id ? null : failure.id)}
                      >
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-red-100 rounded-lg">
                            <XCircleIcon className="w-4 h-4 text-red-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{failure.errorCode}</p>
                            <p className="text-sm text-gray-500">{failure.errorMessage}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-500">{formatRelativeTime(failure.timestamp)}</p>
                          <p className="text-xs text-gray-400">{failure.serviceAccountName || 'Unknown'}</p>
                        </div>
                      </div>

                      <AnimatePresence>
                        {expandedFailure === failure.id && failure.rawPayload && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="px-4 pb-4">
                              <p className="text-xs text-gray-500 mb-2">Raw Payload:</p>
                              <pre className="bg-gray-900 text-green-400 rounded-lg p-4 text-xs font-mono overflow-x-auto max-h-48">
                                {JSON.stringify(failure.rawPayload, null, 2)}
                              </pre>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-32 text-gray-400">
                  <CheckCircleIcon className="w-12 h-12 mb-2" />
                  <p>No recent failures</p>
                </div>
              )}
            </motion.div>
          </PageTransitionItem>
        </>
      )}

      {/* ==================== EHRs TAB ==================== */}
      {activeTab === 'ehrs' && (
        <>
          {/* Actions Bar */}
          <PageTransitionItem>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="font-semibold text-gray-900">EHR Systems</h2>
                <p className="text-sm text-gray-500">
                  Data-driven registry of the EHR vendors that back service accounts and connectors.
                </p>
              </div>
              <button
                onClick={() => { setEditEhrTarget(null); setShowEhrModal(true); }}
                disabled={!ehrCreation.enabled}
                title={!ehrCreation.enabled
                  ? `${ehrCreation.reason ?? 'Not available in this environment.'} Existing systems can still be edited.`
                  : undefined}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  ehrCreation.enabled
                    ? 'bg-purple-600 text-white hover:bg-purple-700 shadow-lg shadow-purple-500/25'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                <PlusIcon className="w-4 h-4" />
                Add EHR System
              </button>
            </div>
          </PageTransitionItem>

          {ehrError && (
            <PageTransitionItem>
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {ehrError}
              </div>
            </PageTransitionItem>
          )}

          {/* EHR Systems Table */}
          <PageTransitionItem>
            <DataTable
              data={sortedEhrs}
              columns={ehrColumns}
              actions={ehrActions}
              loading={!ehrsLoaded}
              emptyMessage="No EHR systems configured"
              emptyIcon={<RectangleStackIcon className="h-12 w-12 text-gray-300" />}
              sortable={true}
              sortState={ehrSortState}
              onSortChange={setEhrSortState}
              hoverable
              striped={false}
              getRowId={(ehr) => ehr.slug}
              ariaLabel="EHR systems table"
            />
          </PageTransitionItem>
        </>
      )}

      {/* ==================== WEBHOOKS TAB ==================== */}
      {activeTab === 'webhooks' && (
        <>
          <PageTransitionItem>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="font-semibold text-gray-900">Webhooks</h2>
                <p className="text-sm text-gray-500">
                  Shared outbound delivery targets. Assign organizations to a webhook, or set one as an EHR default.
                </p>
              </div>
              <button
                onClick={() => { setEditWebhookTarget(null); setShowWebhookModal(true); }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl text-sm font-medium hover:bg-purple-700 transition-colors shadow-lg shadow-purple-500/25"
              >
                <PlusIcon className="w-4 h-4" />
                Add Webhook
              </button>
            </div>
          </PageTransitionItem>

          {webhookError && (
            <PageTransitionItem>
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{webhookError}</div>
            </PageTransitionItem>
          )}

          <PageTransitionItem>
            <div role="tablist" aria-label="Webhook status" className="flex gap-2 border-b border-gray-200">
              {WEBHOOK_VIEWS.map(view => {
                const selected = webhookView === view;
                const count = webhookViewCounts[view];
                return (
                  <button
                    key={view}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    onClick={() => setWebhookView(view)}
                    className={`-mb-px px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      selected ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {WEBHOOK_VIEW_LABELS[view]}
                    {count !== null && <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{count}</span>}
                  </button>
                );
              })}
            </div>
          </PageTransitionItem>

          <PageTransitionItem>
            <DataTable
              data={webhookPageRows}
              columns={webhookColumns}
              actions={webhookActions}
              loading={!webhookPageLoaded}
              emptyMessage={`No ${WEBHOOK_VIEW_LABELS[webhookView].toLowerCase()} webhooks`}
              emptyIcon={<BoltIcon className="h-12 w-12 text-gray-300" />}
              // The server pages in name order; sorting a column would reorder only this page.
              sortable={false}
              pagination={webhookPagination}
              onPageChange={(page) => setWebhookPagination(prev => ({ ...prev, page }))}
              onLimitChange={(limit) => setWebhookPagination(prev => ({ ...prev, limit, page: 1 }))}
              hoverable
              striped={false}
              getRowId={(w) => w.id}
              ariaLabel="Webhooks table"
            />
          </PageTransitionItem>
        </>
      )}

      {/* Modals */}
      <AnimatePresence>
        {showEhrModal && (
          <EhrManagementModal
            isOpen={showEhrModal}
            onClose={() => { setShowEhrModal(false); setEditEhrTarget(null); }}
            ehr={editEhrTarget}
            webhooks={webhooks}
            onSaved={() => {
              fetchEhrs();
              refetchEhrRegistry();
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showWebhookModal && (
          <WebhookManagementModal
            isOpen={showWebhookModal}
            webhook={editWebhookTarget}
            onClose={() => { setShowWebhookModal(false); setEditWebhookTarget(null); }}
            onSaved={() => refreshWebhooks()}
            onSecret={(name, secret) => setRevealedSecret({ name, secret })}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {assignOrgsTarget && (
          <WebhookOrgsModal
            webhook={assignOrgsTarget}
            organizations={organizations as any}
            onClose={() => setAssignOrgsTarget(null)}
            onSaved={() => { refreshWebhooks(); setAssignOrgsTarget(null); }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {revealedSecret && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-xl"><KeyIcon className="w-5 h-5 text-amber-600" /></div>
                <h3 className="text-lg font-semibold text-gray-900">Webhook secret</h3>
              </div>
              <p className="mt-4 text-sm text-gray-600">
                Save this secret for <span className="font-semibold text-gray-900">{revealedSecret.name}</span> now.
                It will not be shown again.
              </p>
              <div className="mt-3 flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-gray-100 rounded-lg text-sm font-mono break-all">{revealedSecret.secret}</code>
                <button
                  onClick={() => navigator.clipboard?.writeText(revealedSecret.secret)}
                  className="p-2 text-gray-600 hover:text-purple-600 border border-gray-300 rounded-lg"
                  title="Copy"
                >
                  <ClipboardDocumentIcon className="w-5 h-5" />
                </button>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setRevealedSecret(null)}
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {deleteWebhookTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-xl">
                  <TrashIcon className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Delete webhook</h3>
              </div>
              <p className="mt-4 text-sm text-gray-600">
                Delete <span className="font-semibold text-gray-900">{deleteWebhookTarget.name}</span>? It is inactive,
                no organization uses it and it has never delivered anything. This cannot be undone.
              </p>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  onClick={() => setDeleteWebhookTarget(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const target = deleteWebhookTarget;
                    setDeleteWebhookTarget(null);
                    handleWebhookDelete(target);
                  }}
                  disabled={webhookActionId === deleteWebhookTarget.id}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  <TrashIcon className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {deactivateEhrTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-xl">
                  <PowerIcon className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Deactivate EHR system</h3>
              </div>
              <p className="mt-4 text-sm text-gray-600">
                Deactivate <span className="font-semibold text-gray-900">{deactivateEhrTarget.displayName}</span>{' '}
                (<span className="font-mono">{deactivateEhrTarget.slug}</span>)? It will be hidden from EHR
                dropdowns across the platform. Encounter ingestion for any organization on this EHR will be
                rejected, and it cannot be deactivated while active service accounts still depend on it.
              </p>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  onClick={() => setDeactivateEhrTarget(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const slug = deactivateEhrTarget.slug;
                    setDeactivateEhrTarget(null);
                    handleEhrLifecycle(slug, 'deactivate');
                  }}
                  disabled={ehrActionSlug === deactivateEhrTarget.slug}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  <PowerIcon className="w-4 h-4" />
                  Deactivate
                </button>
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCreateModal && (
          <CreateServiceAccountModal
            isOpen={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            organizations={organizations}
            partners={partners}
            onCreated={() => fetchServiceAccounts()}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {revokeTarget && (
          <RevokeConfirmationModal
            isOpen={!!revokeTarget}
            onClose={() => setRevokeTarget(null)}
            serviceAccount={revokeTarget}
            onRevoked={() => {
              fetchServiceAccounts();
              fetchDashboardData();
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {renewTarget && (
          <RenewServiceAccountModal
            isOpen={!!renewTarget}
            onClose={() => setRenewTarget(null)}
            serviceAccount={renewTarget}
            onRenewed={() => {
              fetchServiceAccounts();
              fetchDashboardData();
            }}
          />
        )}
      </AnimatePresence>
    </PageTransition>
  );
};

export default ExternalIntegrationsDashboard;
